import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchRecordings,
  insertRecording,
  updateRecording,
  deleteRecording,
  toggleRecordingStatus,
} from "../../../store/recordingsSlice";
import { fetchICTData } from "../../../store/uiSlice";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../../Styles/Common.css";

const RecordingsManagement = () => {
  const dispatch = useDispatch();
  const { recordings, loading } = useSelector((state) => state.recordings);
  const { ictData } = useSelector((state) => state.ui);

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredRecordings, setFilteredRecordings] = useState([]);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [formData, setFormData] = useState({
    recordingLink: "",
    recordingTitle: "",
    recordingSrc: "",
    recordingDateTime: "",
    recordingStatus: "A",
    ictId_id: "",
    trainingId: "",
  });
  // New filter state based on ICT name
  const [currentICTName, setCurrentICTName] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [sortBy, setSortBy] = useState("id"); // Default sort by ID

  useEffect(() => {
    dispatch(fetchRecordings());
    dispatch(fetchICTData());
  }, [dispatch]);

  useEffect(() => {
    let sortedRecordings = [...recordings];

    if (sortBy === "id") {
      sortedRecordings.sort((a, b) => a.id - b.id);
    } else if (sortBy === "ictId_id") {
      sortedRecordings.sort((a, b) => a.ictId_id - b.ictId_id);
    } else if (sortBy === "recordingTitle") {
      sortedRecordings.sort((a, b) =>
        a.recordingTitle.toLowerCase().localeCompare(b.recordingTitle.toLowerCase())
      );
    }

    const filtered = sortedRecordings.filter((recording) => {
      const matchesTitle = recording.recordingTitle.toLowerCase().includes(searchTerm.toLowerCase());
      const ictName = ictData.find((ict) => ict.id === recording.ictId_id)?.ictName || "";
      const matchesICT = currentICTName ? ictName.toLowerCase().includes(currentICTName.toLowerCase()) : true;
      return matchesTitle && matchesICT;
    });

    setFilteredRecordings(filtered);
    setCurrentPage(1); // Reset to first page on search or sort change
  }, [recordings, searchTerm, sortBy, currentICTName, ictData]);

  const handleSearch = (e) => setSearchTerm(e.target.value);
  const handleSortChange = (e) => setSortBy(e.target.value);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddClick = () => {
    resetForm();
    setIsEdit(false);
    setPopupOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) {
        await dispatch(updateRecording(formData)).unwrap();
        toast.success("Recording updated successfully!");
      } else {
        await dispatch(insertRecording(formData)).unwrap();
        toast.success("Recording added successfully!");
      }
      setPopupOpen(false);
      resetForm();
    } catch (error) {
      toast.error("Failed to save recording.");
    }
  };

  const handleEdit = (recording) => {
    setIsEdit(true);
    setPopupOpen(true);
    setFormData(recording);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this recording?")) {
      try {
        await dispatch(deleteRecording(id)).unwrap();
        toast.success("Recording deleted successfully!");
      } catch {
        toast.error("Failed to delete recording.");
      }
    }
  };

  const handleStatusToggle = async (id, status) => {
    try {
      await dispatch(toggleRecordingStatus({ id, status })).unwrap();
      toast.success(
        `Recording status updated to ${status === "A" ? "Active" : "Deactivated"}!`
      );
    } catch (error) {
      toast.error("Failed to update recording status.");
    }
  };

  const resetForm = () => {
    setFormData({
      recordingLink: "",
      recordingTitle: "",
      recordingSrc: "",
      recordingDateTime: "",
      recordingStatus: "A",
      ictId_id: "",
      trainingId: "",
    });
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRecordings.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRecordings.length / itemsPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="container">
      <div className="header-container">
        <h3>Recordings Management</h3>
        <button className="add-lead-btn" onClick={handleAddClick}>
          <i className="fa fa-plus"></i>
        </button>
      </div>
      <div className="feedback-filters">
        <input
          type="text"
          placeholder="Search by Title"
          value={searchTerm}
          onChange={handleSearch}
          className="filter-input"
        />
        <select value={sortBy} onChange={handleSortChange} className="filter-input">
          <option value="id">Sort by ID</option>
          <option value="ictId_id">Sort by ICT ID</option>
          <option value="recordingTitle">Sort by Title</option>
        </select>
        {/* New ICT Name filter dropdown */}
        <select
          value={currentICTName}
          onChange={(e) => setCurrentICTName(e.target.value)}
          className="filter-input"
        >
          <option value="">All ICTs</option>
          {ictData.map((ict) => (
            <option key={ict.id} value={ict.ictName}>
              {ict.ictName}
            </option>
          ))}
        </select>
      </div>
      {loading ? (
        <p>Loading recordings...</p>
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Title - Link</th>
                <th>Date</th>
                <th>Status</th>
                <th>ICT</th>
                <th>Source</th>
                <th>trainingId</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((recording, index) => (
                <tr key={recording.id}>
                  <td>{indexOfFirstItem + index + 1}</td>
                  <td>
                    <a
                      href={recording.recordingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {recording.recordingTitle}
                    </a>
                  </td>
                  <td>{new Date(recording.recordingDateTime).toLocaleString()}</td>
                  <td>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={recording.recordingStatus === "A"}
                        onChange={() =>
                          handleStatusToggle(
                            recording.id,
                            recording.recordingStatus === "A" ? "D" : "A"
                          )
                        }
                      />
                      <span className="slider round"></span>
                    </label>
                  </td>
                  <td>
                    {ictData.find((ict) => ict.id === recording.ictId_id)?.ictName || "N/A"}
                  </td>
                  <td>{recording.recordingSrc}</td>
                  <td>{recording.trainingId}</td>
                  <td>
                    <button
                      className="icon-btn update-btn fa fa-edit"
                      onClick={() => handleEdit(recording)}
                    ></button>
                    <button
                      className="icon-btn delete-btn fa fa-trash"
                      onClick={() => handleDelete(recording.id)}
                    ></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pagination">
            {[...Array(totalPages).keys()].map((page) => (
              <button
                key={page + 1}
                className={`pagination-btn ${currentPage === page + 1 ? "active" : ""}`}
                onClick={() => handlePageChange(page + 1)}
              >
                {page + 1}
              </button>
            ))}
          </div>
        </>
      )}
      {isPopupOpen && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>{isEdit ? "Edit Recording" : "Add Recording"}</h3>
            <form onSubmit={handleSubmit} className="form-grid">
                <div className="form-group">
                  <label>ICT ID</label>
                  <select
                    name="ictId_id"
                    value={formData.ictId_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select ICT</option>
                    {ictData.map((ict) => (
                      <option key={ict.id} value={ict.id}>
                        {ict.ictName}
                      </option>
                    ))}
                  </select>
                  <label>Training Id</label>
                  <input
                    type="number"
                    name="trainingId"
                    value={formData.trainingId}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input
                    type="datetime-local"
                    name="recordingDateTime"
                    value={formData.recordingDateTime}
                    onChange={handleInputChange}
                    required
                  />
                  <label>Status</label>
                  <select
                    name="recordingStatus"
                    value={formData.recordingStatus}
                    onChange={handleInputChange}
                  >
                    <option value="A">Active</option>
                    <option value="D">Deactivated</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Recording Link</label>
                  <input
                    type="text"
                    name="recordingLink"
                    value={formData.recordingLink}
                    onChange={handleInputChange}
                    required
                  />
                  <label>Recording Source</label>
                  <input
                    type="text"
                    name="recordingSrc"
                    value={formData.recordingSrc}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                <label>Title</label>
                  <input
                    type="text"
                    name="recordingTitle"
                    value={formData.recordingTitle}
                    onChange={handleInputChange}
                    required
                  />
                  <div className="form-actions">
                    <button type="submit" className="submit-btn">
                      {isEdit ? "Update" : "Add"}
                    </button>
                    <button type="button" className="cancel-btn" onClick={() => setPopupOpen(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
            </form>
          </div>
        </div>
      )}
      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
};

export default RecordingsManagement;
